import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { API_URL } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, KeyRound } from 'lucide-react';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Mot de passe oublié | Espace Apporteur - CAMBC Conseils</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-slate-100 to-slate-200">
        <div className="max-w-md mx-auto px-4">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors mb-6">
            <ArrowLeft size={18} />
            Retour à la connexion
          </Link>

          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-900 text-white rounded-t-lg text-center pb-8">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound size={32} className="text-white" />
              </div>
              <CardTitle className="text-2xl">Mot de passe oublié</CardTitle>
              <p className="text-blue-100 mt-2">Réinitialisez votre mot de passe</p>
            </CardHeader>
            <CardContent className="p-8">
              {success ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Demande envoyée !
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Si cette adresse email est associée à un compte, vous recevrez les instructions 
                    pour réinitialiser votre mot de passe.
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    Pensez à vérifier vos spams si vous ne recevez pas l'email.
                  </p>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note :</strong> Si vous n'avez pas reçu d'email sous quelques minutes, 
                      contactez-nous au <a href="tel:0582819120" className="text-blue-600 hover:underline">05 82 81 91 20</a>.
                    </p>
                  </div>
                  <Link to="/login">
                    <Button className="mt-6 bg-blue-700 hover:bg-blue-800">
                      Retour à la connexion
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
                      <AlertCircle size={18} />
                      {error}
                    </div>
                  )}

                  <p className="text-slate-600 mb-6">
                    Entrez votre adresse email ci-dessous. Nous vous enverrons un lien pour 
                    réinitialiser votre mot de passe.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base font-semibold">
                        Adresse email
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="votre@email.fr"
                          required
                          className="h-12 pl-10"
                          data-testid="forgot-email"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white text-lg py-6"
                      disabled={loading}
                      data-testid="forgot-submit"
                    >
                      {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
                    </Button>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
